import { useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { FileWithPreview } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileUploadConfiguration.types';
import { uploadFileToSource } from 'js/actions/sources';
import {
  generateFileId,
  validateFileSize,
} from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/utils/fileUtils';
import {
  getMaxFilesLimit,
  getBlockedExtensions,
} from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/config/fileUploadConfig';
import { MAX_UPLOAD_FILE_SIZE } from 'js/constants/config';

export interface FileValidationError {
  type: 'max_files' | 'file_type' | 'file_size';
  message: string;
  fileName?: string;
}

interface UseSourceNodeFileUploadOptions {
  sourceId: number;
  onFilesChange?: (files: FileWithPreview[]) => void;
  onValidationErrors?: (errors: FileValidationError[]) => void;
}

interface UseSourceNodeFileUploadResult {
  files: FileWithPreview[];
  dragHandlers: {
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
  };
  dropZoneRef: React.RefObject<HTMLDivElement>;
  handleFileSelect: () => void;
  handleFileClick: (file: FileWithPreview) => void;
  removeFile: (fileId: string) => void;
  retryFile: (fileId: string) => void;
  previewFile: FileWithPreview | null;
  setPreviewFile: (file: FileWithPreview | null) => void;
  startUpload: () => Promise<void>;
  isUploading: boolean;
  removeAllFilesAndReset: () => void;
}

export function useSourceNodeFileUpload(
  options: UseSourceNodeFileUploadOptions,
): UseSourceNodeFileUploadResult {
  const { sourceId, onFilesChange, onValidationErrors } = options;
  const dispatch = useDispatch();
  const maxFiles = getMaxFilesLimit();
  const blockedExtensions = getBlockedExtensions();
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [previewFile, setPreviewFile] = useState<FileWithPreview | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const filesRef = useRef<FileWithPreview[]>(files);
  filesRef.current = files;

  const simulateProgress = useCallback(
    (fileId: string, type: 'upload' | 'backend', duration: number) => {
      return new Promise<void>((resolve) => {
        const startTime = Date.now();
        const interval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min((elapsed / duration) * 100, 100);

          setFiles((prev) =>
            prev.map((f) => {
              if (f.id === fileId) {
                return {
                  ...f,
                  [type === 'upload' ? 'uploadProgress' : 'backendUploadProgress']: progress,
                };
              }
              return f;
            }),
          );

          if (progress >= 100) {
            clearInterval(interval);
            resolve();
          }
        }, 50);
      });
    },
    [],
  );

  const uploadFile = useCallback(
    async (file: FileWithPreview) => {
      const fileId = file.id;

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                status: 'uploading' as const,
                uploadProgress: 0,
                backendUploadProgress: 0,
              }
            : f,
        ),
      );

      try {
        const sizeInMB = Math.max(1, (file.size || 0) / (1024 * 1024));
        const uploadDuration = Math.min(10000, Math.max(800, Math.round(sizeInMB * 500)));
        const backendDuration = Math.min(12000, Math.max(1000, Math.round(sizeInMB * 700)));

        const uploadSim = simulateProgress(fileId, 'upload', uploadDuration);
        const uploadPromise = dispatch<any>(uploadFileToSource(sourceId, file as File));

        await uploadSim;
        setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, uploadProgress: 100 } : f)));

        const backendSim = simulateProgress(fileId, 'backend', backendDuration);
        const { result: upload } = await uploadPromise;
        await backendSim;

        const isSuccess = upload?.message?.toLowerCase?.() === 'success' || upload?.status === 200;

        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? {
                  ...f,
                  status: isSuccess ? ('uploaded' as const) : ('error' as const),
                  backendUploadProgress: 100,
                }
              : f,
          ),
        );
      } catch (error) {
        setFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, status: 'error' as const } : f)),
        );
      }
    },
    [sourceId, dispatch, simulateProgress],
  );

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      const errors: FileValidationError[] = [];

      setFiles((prev) => {
        const currentUploadableCount = prev.filter((f) => !f.isBlocked && !f.isOverLimit).length;
        let availableSlots = Math.max(0, maxFiles - currentUploadableCount);
        const toAdd: FileWithPreview[] = [];

        for (const file of fileArray) {
          const fileId = generateFileId(file);

          if (filesRef.current.some((f) => f.id === fileId)) {
            errors.push({
              type: 'file_type',
              message: `"${file.name}" is already selected`,
              fileName: file.name,
            });
            continue;
          }

          const base = {
            id: fileId,
            status: 'pending' as const,
            uploadProgress: 0,
            backendUploadProgress: 0,
            isBlocked: false,
            isOverLimit: false,
            exclusionReason: undefined as string | undefined,
          };
          let isExcluded = false;

          if (MAX_UPLOAD_FILE_SIZE) {
            const sizeResult = validateFileSize(file, MAX_UPLOAD_FILE_SIZE);
            if (!sizeResult.valid) {
              base.isBlocked = true;
              base.exclusionReason = sizeResult.error || 'File size exceeds limit';
              isExcluded = true;
              errors.push({
                type: 'file_size',
                message: sizeResult.error || 'File size exceeds limit',
                fileName: file.name,
              });
            }
          }

          const ext = file.name.includes('.')
            ? `.${file.name.split('.').pop()?.toLowerCase()}`
            : '';
          if (!isExcluded && blockedExtensions.length > 0 && blockedExtensions.includes(ext)) {
            base.isBlocked = true;
            base.exclusionReason = `File type "${ext}" is not allowed`;
            isExcluded = true;
            errors.push({
              type: 'file_type',
              message: `File type "${ext}" is not allowed`,
              fileName: file.name,
            });
          }

          if (!isExcluded && availableSlots <= 0) {
            base.isOverLimit = true;
            base.exclusionReason = `Maximum of ${maxFiles} files allowed`;
            isExcluded = true;
            errors.push({
              type: 'max_files',
              message: `Maximum of ${maxFiles} files allowed`,
              fileName: file.name,
            });
          } else if (!isExcluded) {
            availableSlots--;
          }

          toAdd.push(Object.assign(file, base) as unknown as FileWithPreview);
        }

        if (errors.length > 0) {
          onValidationErrors?.(errors);
        }

        const updated = [...prev, ...toAdd];
        onFilesChange?.(updated);
        return updated;
      });
    },
    [onFilesChange, onValidationErrors, maxFiles, blockedExtensions],
  );

  const startUpload = useCallback(async () => {
    const pendingFiles = files.filter(
      (f) => f.status === 'pending' && !f.isBlocked && !f.isOverLimit,
    );
    if (pendingFiles.length === 0) return;

    setIsUploading(true);
    try {
      for (const file of pendingFiles) {
        await uploadFile(file);
      }
      // Show success state briefly, then clear selection
      const successDisplayMs = 1500;
      await new Promise((resolve) => setTimeout(resolve, successDisplayMs));
      setFiles([]);
      onFilesChange?.([]);
    } finally {
      setIsUploading(false);
    }
  }, [files, uploadFile, onFilesChange]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        addFiles(droppedFiles);
      }
    },
    [addFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const internalDragHandlers = {
    onDragOver: handleDragOver,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  const handleFileSelect = useCallback(() => {
    // This will be handled by the component using the ref
  }, []);

  const removeFile = useCallback(
    (fileId: string) => {
      setFiles((prev) => {
        const updated = prev.filter((f) => f.id !== fileId);
        onFilesChange?.(updated);
        return updated;
      });
    },
    [onFilesChange],
  );

  const removeAllFilesAndReset = useCallback(() => {
    setFiles([]);
    onFilesChange?.([]);
  }, [onFilesChange]);

  const handleFileClick = useCallback((file: FileWithPreview) => {
    setPreviewFile(file);
  }, []);

  const retryFile = useCallback((fileId: string) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileId
          ? {
              ...f,
              status: 'pending' as const,
              uploadProgress: 0,
              backendUploadProgress: 0,
            }
          : f,
      ),
    );
  }, []);

  return {
    files,
    dragHandlers: internalDragHandlers,
    dropZoneRef,
    handleFileSelect,
    handleFileClick,
    removeFile,
    retryFile,
    previewFile,
    setPreviewFile,
    startUpload,
    isUploading,
    removeAllFilesAndReset,
  };
}
